export interface PrescriptionSummarySection {
  title: string;
  body: string;
}

/** Remove markdown bold and numbered list prefixes from summary text. */
export function stripSummaryFormatting(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^\s*\d+[.)]\s+/gm, "- ")
    .trim();
}

export function formatSectionBody(text: string): string {
  return stripSummaryFormatting(text);
}

export function parsePrescriptionSummarySections(text: string): PrescriptionSummarySection[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const chunks = (
    /\*\*[^*\n]+\*\*/.test(trimmed)
      ? trimmed.split(/(?=\*\*[^*\n]+\*\*:?\s*)/)
      : trimmed.split(/(?=^[A-Za-z][^:\n]{1,80}:\s*)/m)
  )
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  const sections: PrescriptionSummarySection[] = [];

  chunks.forEach((chunk) => {
    const markdownHeaderMatch = chunk.match(/^\*\*([^*]+)\*\*:?\s*([\s\S]*)/);
    if (markdownHeaderMatch) {
      sections.push({
        title: markdownHeaderMatch[1].trim(),
        body: formatSectionBody(markdownHeaderMatch[2])
      });
      return;
    }

    const colonHeaderMatch = chunk.match(/^([^:\n]{2,80}):\s*([\s\S]*)/);
    if (colonHeaderMatch) {
      sections.push({
        title: colonHeaderMatch[1].trim(),
        body: formatSectionBody(colonHeaderMatch[2])
      });
      return;
    }

    sections.push({
      title: "",
      body: formatSectionBody(chunk)
    });
  });

  return sections.filter((section) => section.title || section.body);
}

/** Normalize summary for display and storage: no ** markers, no numbered lists. */
export function formatPrescriptionSummaryText(text: string): string {
  const sections = parsePrescriptionSummarySections(text);
  if (sections.length === 0) {
    return stripSummaryFormatting(text);
  }

  return sections
    .map((section) => (section.title ? `${section.title}:\n${section.body}` : section.body))
    .filter(Boolean)
    .join("\n\n");
}
